import type { User } from "firebase/auth";
import { GoogleAuthProvider, signInAnonymously, signInWithPopup } from "firebase/auth";
import { ArrowLeft, ArrowRight, Gamepad2, KeyRound, ShieldCheck, TriangleAlert } from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { friendlyError, gameApi, type RoomEntry } from "../lib/api";
import { auth } from "../lib/firebase";
import { preloadRoute } from "../lib/routePreload";
import { imageFileError } from "../lib/imageFile";
import { normalizedPlayerName, playerNameInput, validPlayerName } from "../lib/playerName";
import { uploadAvatar } from "../lib/storage";
import { getQuest } from "../data/quests";
import { usePlayerProfile } from "../hooks/usePlayerProfile";
import { AvatarArtwork, AvatarChooser, randomBuiltInAvatar } from "./AvatarPicker";
import { JourneyShell } from "./JourneyShell";
import { QuestExplanation } from "./QuestExplanation";
import "./RoomRecovery.css";
import "./HomePage.css";

type EntryStep = { kind: "details" }
  | { kind: "setup" | "password"; code: string; name: string; requestedName: string }
  | { kind: "takeover"; code: string; name: string; requestedName: string; requiresPassword: boolean };
const cleanRoomCode = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);

export function HomePage({ user }: { user: User | null | undefined }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const roomQuery = searchParams.get("room");
  const quest = getQuest(searchParams.get("quest"));
  const previousQuest = useRef(quest?.id);
  const joinParams = new URLSearchParams(searchParams);
  joinParams.delete("quest");
  const joinHref = joinParams.size ? `/?${joinParams.toString()}` : "/";
  const [roomCode, setRoomCode] = useState(cleanRoomCode(roomQuery || ""));
  const [name, setName] = useState("");
  // Guests are dealt a random character; the room's taken avatars refine it on the setup step.
  const [avatar, setAvatar] = useState<string>(() => randomBuiltInAvatar());
  const avatarChosen = useRef(false);
  const takenAvatars = useRef<string[]>([]);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<"entry" | "google" | "">("");
  const [step, setStep] = useState<EntryStep>({ kind: "details" });
  const [password, setPassword] = useState("");
  const [protectSession, setProtectSession] = useState(false);
  const { name: savedName, avatar: savedAvatar, loading: profileLoading, error: profileError, retry: retryProfile } = usePlayerProfile(user);
  const profileUid = user && !user.isAnonymous ? user.uid : null;
  const nameEdited = useRef(false);
  const navigate = useNavigate();
  const request = useRef(0);
  const joiningLock = useRef(false);
  const mounted = useRef(false);
  const joining = Boolean(busy);

  useEffect(() => {
    if (!profileUid || !savedAvatar) return;
    setName(normalizedPlayerName(savedName));
    setAvatar(savedAvatar);
    setAvatarFile(null);
    setAvatarPreview("");
    setProtectSession(false);
    setPassword("");
  }, [profileUid, savedName, savedAvatar]);

  useEffect(() => {
    if (!profileUid) return;
    setStep((current) => current.kind === "takeover" || current.kind === "password" ? { kind: "details" } : current);
  }, [profileUid]);

  useEffect(() => {
    if (previousQuest.current !== quest?.id) {
      request.current += 1;
      joiningLock.current = false;
      setBusy("");
      document.getElementById(quest ? "quest-explanation-title" : "join-title")?.focus();
      previousQuest.current = quest?.id;
    }
  }, [quest]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; request.current += 1; joiningLock.current = false; };
  }, []);

  useEffect(() => {
    if (roomQuery) {
      setRoomCode(cleanRoomCode(roomQuery));
      setStep({ kind: "details" });
      setPassword("");
      setProtectSession(false);
      setError("");
      request.current += 1;
      joiningLock.current = false;
      setBusy("");
    }
  }, [roomQuery]);

  useEffect(() => {
    if (step.kind === "details" && nameEdited.current && !profileUid) {
      const input = document.getElementById("player-name") as HTMLInputElement | null;
      input?.focus({ preventScroll: true });
      input?.select();
    } else if (step.kind !== "details") document.getElementById(step.kind === "password" ? "room-password" : "join-title")?.focus({ preventScroll: true });
  }, [step.kind]);

  useEffect(() => () => { if (avatarPreview) URL.revokeObjectURL(avatarPreview); }, [avatarPreview]);

  function selectBuiltIn(nextAvatar: string) {
    avatarChosen.current = true;
    setAvatar(nextAvatar);
    setAvatarFile(null);
    setAvatarPreview("");
    setError("");
  }

  function chooseAvatarFile(file: File) {
    if (joiningLock.current) return;
    const fileError = imageFileError(file);
    if (fileError) { setError(fileError); return; }
    setError("");
    avatarChosen.current = true;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  }

  function shuffleAvatar() {
    if (joiningLock.current) return;
    avatarChosen.current = true;
    setAvatar(randomBuiltInAvatar([...takenAvatars.current, avatar]));
    setAvatarFile(null);
    setAvatarPreview("");
    setError("");
  }

  function backToDetails() {
    setStep({ kind: "details" });
    setPassword("");
    setProtectSession(false);
    setError("");
    document.getElementById("join-title")?.focus({ preventScroll: true });
  }

  function continueEntry(result: RoomEntry) {
    setPassword("");
    if (result.status === "joined") navigate(`/room/${result.code}`);
    else if (result.status === "takeover_required" || (result.status === "password_required" && step.kind === "takeover")) {
      setStep({ kind: "takeover", code: result.code, name: result.name, requestedName: result.name, requiresPassword: result.status === "takeover_required" ? result.requiresPassword : true });
    } else {
      if (result.status === "setup_required") {
        takenAvatars.current = result.takenAvatars ?? [];
        if (!avatarChosen.current && takenAvatars.current.length) setAvatar(randomBuiltInAvatar(takenAvatars.current));
      }
      setStep({ kind: result.status === "setup_required" ? "setup" : "password", code: result.code, name: result.name, requestedName: result.requestedName ?? result.name });
    }
  }

  async function googleSignIn() {
    if (joiningLock.current) return;
    const attempt = ++request.current;
    joiningLock.current = true;
    setBusy("google");
    setError("");
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      // Keep the room and chosen name while the saved profile loads.
    } catch (caught) {
      if (mounted.current && request.current === attempt) setError(friendlyError(caught));
    } finally {
      if (mounted.current && request.current === attempt) { joiningLock.current = false; setBusy(""); }
    }
  }

  async function join(event: FormEvent) {
    event.preventDefault();
    if (joiningLock.current || (step.kind === "setup" && (profileLoading || profileError))) return;
    setError("");
    const code = step.kind === "details" ? cleanRoomCode(roomCode) : step.code;
    const joinName = profileUid ? normalizedPlayerName(savedName) : step.kind === "details" ? normalizedPlayerName(name) : step.requestedName;
    if (code.length !== 6 || !validPlayerName(joinName)) {
      setError(profileUid
        ? code.length !== 6 ? "Enter a six-character room code." : "Set a player name in My profile before joining."
        : "Enter a six-character room code and your name using letters and spaces.");
      return;
    }
    if ((step.kind === "password" || (step.kind === "takeover" && step.requiresPassword) || (step.kind === "setup" && !profileUid && protectSession)) && (password.length < 8 || password.length > 128)) {
      setError("Use a room password of 8–128 characters.");
      return;
    }
    if (!profileUid) nameEdited.current = true;
    setName(joinName);
    setRoomCode(code);
    const attempt = ++request.current;
    const isCurrent = () => mounted.current && request.current === attempt;
    let playerUid: string | null = null;
    joiningLock.current = true;
    setBusy("entry");
    preloadRoute(`/room/${code}`);
    try {
      const currentUser = auth.currentUser || (await signInAnonymously(auth)).user;
      if (!isCurrent()) return;
      playerUid = currentUser.uid;
      const sameSession = () => isCurrent() && auth.currentUser?.uid === playerUid;
      if (!sameSession()) throw new Error("Your sign-in changed. Check your details and try again.");
      if (step.kind === "setup") {
        // Logging in may select an account with an existing player. Recheck both
        // identity and room state before uploading or completing a new profile.
        const entry = await gameApi.enterRoom({ code, name: joinName });
        if (!sameSession()) throw new Error("Your sign-in changed. Check your details and try again.");
        if (entry.status !== "setup_required") { continueEntry(entry); return; }
        if (entry.name !== step.name) { continueEntry(entry); return; }
        const chosenAvatar = !currentUser.isAnonymous
          ? savedAvatar
          : avatarFile ? await uploadAvatar(currentUser.uid, avatarFile) : avatar;
        if (!chosenAvatar) throw new Error("Your profile is still loading. Please try again.");
        if (!sameSession()) throw new Error("Your sign-in changed. Check your details and try again.");
        try {
          await gameApi.joinRoom({ code, name: joinName, avatar: chosenAvatar, ...(currentUser.isAnonymous && protectSession ? { password } : {}) });
        } catch (caught) {
          // Another browser may have taken this name while the setup card was open.
          if (caught && typeof caught === "object" && "code" in caught && caught.code === "functions/already-exists" && sameSession()) {
            const result = await gameApi.enterRoom({ code, name: joinName });
            if (sameSession()) continueEntry(result);
            return;
          }
          throw caught;
        }
        if (!sameSession()) throw new Error("Your sign-in changed. Check your details and try again.");
        setPassword("");
        navigate(`/room/${code}`);
      } else {
        const result = await gameApi.enterRoom({ code, name: joinName, ...(step.kind === "password" || (step.kind === "takeover" && step.requiresPassword) ? { password } : {}), ...(step.kind === "takeover" ? { confirmTakeover: true } : {}) });
        if (!sameSession()) throw new Error("Your sign-in changed. Check your details and try again.");
        continueEntry(result);
      }
    } catch (caught) {
      if (isCurrent()) setError(playerUid && auth.currentUser?.uid !== playerUid ? "Your sign-in changed. Check your details and try again." : friendlyError(caught));
    } finally {
      if (isCurrent()) { joiningLock.current = false; setBusy(""); }
    }
  }

  const title = step.kind === "details" ? "Jump right in" : step.kind === "takeover" ? "This name is already taken" : step.kind === "password" ? "Welcome back" : "Make yourself at home";
  const submitLabel = busy === "google" ? "Signing in…" : busy === "entry" ? "Checking in…" : step.kind === "details" ? "Continue" : step.kind === "takeover" ? "Replace player and join" : step.kind === "password" ? "Rejoin room" : profileLoading ? "Loading your profile…" : "Join room";
  const returningPlayer = step.kind === "password" || step.kind === "takeover";
  const removedFromRoom = location.state?.removedFromRoom === roomCode && step.kind === "details";

  return (
    <JourneyShell user={user} profileAvatar={savedAvatar}>
      {quest ? <QuestExplanation quest={quest} joinHref={joinHref} /> : <section className={`join-card hub-join-card entry-${step.kind}${roomQuery ? " from-invite" : ""}`} aria-labelledby="join-title" aria-busy={joining}>
        {removedFromRoom && <div className="join-removed-notice" role="alert"><TriangleAlert size={20} aria-hidden="true" /><p>Someone joined using your name. You’ve been removed from room <strong>{roomCode}</strong>.</p></div>}
        {step.kind !== "details" && <button type="button" className="join-back" disabled={joining} onClick={backToDetails}><ArrowLeft size={15} /> {profileUid ? "Change room" : "Change room or name"}</button>}
        <div className="join-card-heading">
          <div><div className="card-kicker">{step.kind === "details" ? "Got a room code?" : step.kind === "takeover" ? "Before you join" : step.kind === "password" ? "Your player is already here" : "One last thing"}</div><h2 id="join-title" tabIndex={-1}>{title}</h2></div>
          <span className="join-icon">{step.kind === "takeover" ? <TriangleAlert size={25} /> : step.kind === "password" ? <KeyRound size={25} /> : <Gamepad2 size={25} />}</span>
        </div>
        <p>{step.kind === "details" ? profileUid ? "Enter the room code. You’ll join with your profile name and photo." : "Enter your room code and the name your team knows you by." : step.kind === "takeover" ? "Someone is already in the waiting room with this name." : step.kind === "password" ? "Enter your room password to pick up where you left off." : profileUid ? "Your profile is ready. Your Google account protects your place in the room." : "We’ve picked an avatar for you. Change it, or log in with Google. Guests can add a room password."}</p>
        {step.kind !== "details" && <div className="join-entry-summary"><span>Room <strong>{step.code}</strong></span><span>Playing as <strong>{step.name}</strong></span></div>}
        {step.kind === "setup" && step.name !== step.requestedName && <p className="join-name-assignment" role="status">{step.requestedName} is already in this room. You’ll join as <strong>{step.name}</strong>.</p>}
        {step.kind === "takeover" && <div className="join-takeover-warning" role="alert"><TriangleAlert size={20} aria-hidden="true" /><div><strong>Joining will kick the other player out.</strong><p>You’ll take over <strong>{step.name}</strong>’s place, and their screen will return to check-in. Choose another name to keep both players in the room.</p></div></div>}
        <form onSubmit={join}>
          <fieldset className="room-join-fieldset" disabled={joining}>
            {step.kind === "details" && <div className={`join-fields${profileUid ? " has-profile" : ""}`}>
              <label htmlFor="room-code"><span>Room code</span><input id="room-code" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={12} placeholder="QST404" value={roomCode} onChange={(event) => { setRoomCode(cleanRoomCode(event.target.value)); setError(""); }} required /></label>
              {profileUid ? <div className="join-profile-identity">
                <span>Your name</span>
                <div className="join-saved-player">
                  <AvatarArtwork avatar={savedAvatar} label="Your profile photo" />
                  <div><strong>{savedName}</strong><span>{profileLoading ? "Loading your saved profile…" : "From your profile"}</span></div>
                </div>
                <Link className="join-profile-edit" to="/account">Change this in My profile</Link>
              </div> : <>
                <label htmlFor="player-name"><span>Your name</span><input id="player-name" autoComplete="name" autoCapitalize="characters" spellCheck={false} maxLength={28} placeholder="SAM" value={name} onChange={(event) => { nameEdited.current = true; setName(playerNameInput(event.target.value)); setError(""); }} aria-describedby="join-name-hint" required /></label>
                <small id="join-name-hint" className="join-field-hint">Letters and spaces only. We’ll use capitals.</small>
              </>}
            </div>}
            {step.kind === "setup" && <>
              {profileUid ? <><div className="join-saved-player"><AvatarArtwork avatar={savedAvatar} label="Your profile photo" /><div><strong>{step.name}</strong><span>{profileLoading ? "Loading your saved profile…" : profileError ? "Your saved profile is unavailable" : "Using your player profile"}</span></div>{!profileError && <ShieldCheck size={20} aria-hidden="true" />}</div>{profileError && <div className="join-profile-error" role="alert"><p>We couldn’t load your saved profile. Try again to use your chosen avatar.</p><button type="button" onClick={retryProfile}>Try again</button></div>}</> : <>
                <button type="button" className="join-google-button" onClick={() => void googleSignIn()} disabled={joining}><span aria-hidden="true">G</span>{busy === "google" ? "Signing in…" : "Continue with Google"}</button>
                <div className="join-divider"><span>or join as a guest</span></div>
                <AvatarChooser value={avatar} previewUrl={avatarPreview} busy={joining} onSelect={selectBuiltIn} onFile={chooseAvatarFile} onShuffle={shuffleAvatar} />
                {avatarFile && <small className="join-field-hint">Guest photo uploads are temporary and are deleted after 24 hours.</small>}
              </>}
              {profileUid ? <p className="join-account-protection"><ShieldCheck size={18} aria-hidden="true" /><span>Only your Google account can rejoin as this player. No room password needed.</span></p> : <>
                <label className="room-password-optin join-password-choice"><input type="checkbox" checked={protectSession} onChange={(event) => { setProtectSession(event.target.checked); setPassword(""); }} /><span>Protect my name with a password<small>Optional. You’ll use it to rejoin on another device.</small></span></label>
                {!protectSession && <p className="join-password-hint">Without a password, another guest using this room code and name can take over your place.</p>}
              </>}
            </>}
            {(step.kind === "password" || (step.kind === "takeover" && step.requiresPassword) || (step.kind === "setup" && !profileUid && protectSession)) && <label className="room-password-field" htmlFor="room-password">Room password<input id="room-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={returningPlayer ? "current-password" : "new-password"} minLength={8} maxLength={128} required placeholder="At least 8 characters" /><small>{step.kind === "takeover" ? "This name is password protected. Enter its room password to take over." : step.kind === "password" ? "Use the password you set for this room, not your Google password." : "Rejoin with the same room code, name, and password. No extra ID to remember."}</small></label>}
          </fieldset>
          {error && <div className="form-error" role="alert">{error}</div>}
          {step.kind === "takeover" && <button type="button" className="join-keep-player" disabled={joining} onClick={backToDetails}>Choose another name</button>}
          <button type="submit" className={step.kind === "takeover" ? "join-confirm-takeover" : undefined} disabled={joining || user === undefined || (step.kind === "setup" && (profileLoading || Boolean(profileError)))}>{submitLabel}<ArrowRight size={18} /></button>
        </form>
        {step.kind === "details" && !profileUid && <p className="join-footer-note">Joining again? Use the same name, or log in to the Google account you used.</p>}
      </section>}
    </JourneyShell>
  );
}
